/**
 * Component Name: Button
 *
 * Description:
 * The Button component is a versatile and reusable link-based button designed to navigate users to different
 * pages or sections of the site. It supports customization for appearance, label, and icon, making it suitable
 * for various use cases, including primary actions, secondary actions, and navigation links.
 *
 * Usage:
 * - Use the Button component to provide a clear call-to-action (CTA) on pages, cards, or forms.
 * - Configure properties like `url`, `label`, `type`, and `icon` to customize its behavior and style.
 *
 * Key Features:
 * - **Routing Support:** Integrates with React Router for navigation using the `Link` component.
 * - **Customizable Types:** Supports `primary`, `secondary`, or other styles defined in SCSS via the `type` prop.
 * - **Optional Icons:** Add an icon after the button label, defaulting to `chevron_right`.
 * - **Reusable Styles:** The `className` prop allows additional custom styling.
 *
 * Example:
 * ```tsx
 * <Button
 *   url="/contact-us"
 *   label="Get in Touch"
 *   type="primary"
 *   icon="mail"
 * />
 * ```
 *
 * Notes:
 * - Ensure `url` is a valid string; defaults to `/` if not provided.
 * - Use SCSS (`Button.styles.scss`) for styling variations, such as button colors, sizes, and hover effects.
 * - The `type` prop controls button appearance but can be overridden with `className` for more specific use cases.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './Button.styles.scss';
import { ComponentProps } from './Button.logic';
import Icon from '../Icon/Icon';

const Button: React.FC<ComponentProps> = ({
  url,
  label,
  className,
  isExternal,
  onClick,
  backgroundColor,
  type = 'primary',
  btnType,
  icon,
}) => {
  const isHttpLink = url?.startsWith('http') || false;

  const linkProps =
    isExternal || isHttpLink
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};

  if (isExternal || isHttpLink) {
    return (
      <a
        href={url || '/'}
        style={{ backgroundColor }}
        className={`button ${className} button-type-${type}`}
        {...linkProps}
      >
        {label}
        <Icon icon={icon} />{' '}
      </a>
    );
  }

  if (url) {
    return (
      <Link
        onClick={onClick}
        to={url || '/'}
        style={{ backgroundColor }}
        className={`button ${className} button-type-${type}`}
      >
        {label}
        <Icon icon={icon ?? 'chevron_right'} />{' '}
        {/* Render the icon, defaults to 'chevron_right' */}
      </Link>
    );
  } else {
    return (
      <button
        onClick={onClick}
        type={btnType}
        style={{ backgroundColor }}
        className={`button ${className} button-type-${type}`}
      >
        {label}
        <Icon icon={icon} />{' '}
        {/* Render the icon, defaults to 'chevron_right' */}
      </button>
    );
  }
};

export default Button;
